// src/pages/ParentPortal.jsx
// Public, no-auth parent-facing view of a single registration's payment
// schedule. The route is /r/:registrationId — the 20-char Firestore auto-ID
// serves as an access token, so only someone with the original
// confirmation / receipt email can open this page. No PII beyond what the
// server-side sanitizer chooses to expose.
'use client';

import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  PencilSquareIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

// Design tokens — keep in sync with RegisterPage and the rest of the
// parent-facing surface. Returning parents see this page every time
// they tap the link in their confirmation email; matching their first-
// touch (RegisterPage) is what makes the brand feel cohesive.
const HEAD_FONT = "'Barlow Condensed', sans-serif";

const T = {
  en: {
    loading: 'Loading…',
    notFound: 'We couldn\u2019t find that registration.',
    notFoundHint: 'The link may be out of date. Please check your confirmation email for the most recent link, or contact your coach.',
    titlePrefix: 'Payment plan for',
    nextCharge: 'Next charge',
    totalPaid: 'Paid so far',
    remaining: 'Remaining',
    scheduleHeading: 'Schedule',
    statusPaid: 'Paid',
    statusPending: 'Scheduled',
    statusFailed: 'Failed',
    statusCancelled: 'Cancelled',
    statusRefunded: 'Refunded',
    statusPartiallyRefunded: 'Partially refunded',
    cancelledHeading: 'This registration was cancelled.',
    cancelledRefunded: (amt) => `A refund of ${amt} has been issued. It may take 5–10 business days to appear on your card.`,
    cancelledNoRefund: 'No refund was issued. Contact your coach if you have questions.',
    paidOn: 'on',
    attempts: 'attempts',
    installment: 'Installment',
    paymentMethod: 'Payment method',
    cardOnFile: 'A card is on file for automatic charges.',
    cardMissing: 'No card is on file. Please contact your coach.',
    paidInFull: 'Paid in full \u2014 thank you!',
    updateCard: 'Update payment method',
    updateCardOpening: 'Opening secure portal\u2026',
    updateCardError: 'Couldn\u2019t open the billing portal. Please try again or contact your coach.',
    pastDueHeading: 'There\u2019s a past-due payment.',
    pastDueBody: 'We tried to charge your card but it didn\u2019t go through. We\u2019ll retry automatically, but you may want to update your card.',
    lastFailure: 'Last error',
    contactCoach: 'Questions? Reach out to your coach.',
    poweredBy: 'Scrimly',
    // Edit flow
    editHeading: 'Update your info',
    editIntro: 'Need to change a phone number, emergency contact, or medical notes? We\u2019ll email a secure link to the address on file.',
    editSendButton: 'Email me an edit link',
    editSending: 'Sending\u2026',
    editSentTitle: 'Check your email',
    editSentBody: (emailMasked) => `We sent a secure edit link to ${emailMasked}. It\u2019s good for 15 minutes and can only be used once.`,
    editSendFailed: 'Couldn\u2019t send the email. Please try again or contact your coach.',
    // Edit mode (after opening the magic link)
    editModeHeading: 'Edit your info',
    editModeIntro: 'Update the fields below. Changes save immediately when you click "Save".',
    editParentName: 'Your name',
    editParentPhone: 'Your phone',
    editParentEmailReadOnly: 'Your email (contact your coach to change)',
    editEmergencyHeading: 'Emergency contact',
    editEmergencyName: 'Name',
    editEmergencyPhone: 'Phone',
    editEmergencyRelationship: 'Relationship',
    editMedicalHeading: 'Medical notes',
    editMedicalHint: 'Allergies, conditions, or anything the coach should know.',
    editSave: 'Save changes',
    editSaving: 'Saving\u2026',
    editCancel: 'Cancel',
    editSaveFailed: 'Save failed. Your edit link may have expired — try requesting a new one.',
    editSaved: 'Your changes were saved.',
    editTokenInvalid: 'This edit link is invalid or expired. Please request a new one from your registration page.',
    // Calendar subscription
    calHeading: 'Subscribe to the team schedule',
    calIntro: 'Add every practice and game to your calendar. Updates happen automatically when your coach changes the schedule.',
    calSubscribe: 'Subscribe',
    calAddToApple: 'Add to Apple Calendar',
    calAddToGoogle: 'Add to Google Calendar',
    calCopyLink: 'Copy link',
    calCopied: 'Copied!',
    // SMS preferences card
    smsHeading: 'Text message updates',
    smsOnLead: (phone) => `You're subscribed at ${phone}.`,
    smsOnBody: 'You\u2019ll get game reminders, RSVP requests, cancellations, and weather alerts.',
    smsOffLead: 'Text updates are off.',
    smsOffBody: (phone) => `Turn on to get reminders, RSVP requests, and weather alerts at ${phone}.`,
    smsNoPhoneBody: 'Add a phone number to your contact info first, then come back here to turn on text updates.',
    smsTurnOn: 'Turn on text updates',
    smsTurnOff: 'Turn off text updates',
    smsSaving: 'Saving\u2026',
    smsConfirmTitle: 'Turn on text updates?',
    smsConfirmDisclosure: 'By turning this on, you agree to receive automated text messages from Scrimly about this registration \u2014 including weather cancellations, schedule changes, RSVP requests, and payment reminders \u2014 at the phone number on file. Message & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of registration.',
    smsConfirmAgree: 'I agree \u2014 turn on',
    smsConfirmCancel: 'Cancel',
    smsError: 'Couldn\u2019t update your text preferences. Please try again.',
  },
  es: {
    loading: 'Cargando…',
    notFound: 'No pudimos encontrar ese registro.',
    notFoundHint: 'El enlace puede estar desactualizado. Por favor verifica tu correo de confirmación o contacta a tu entrenador.',
    titlePrefix: 'Plan de pagos de',
    nextCharge: 'Próximo cobro',
    totalPaid: 'Pagado hasta ahora',
    remaining: 'Restante',
    scheduleHeading: 'Calendario',
    statusPaid: 'Pagado',
    statusPending: 'Programado',
    statusFailed: 'Falló',
    statusCancelled: 'Cancelado',
    statusRefunded: 'Reembolsado',
    statusPartiallyRefunded: 'Reembolso parcial',
    cancelledHeading: 'Este registro fue cancelado.',
    cancelledRefunded: (amt) => `Se emitió un reembolso de ${amt}. Puede tardar 5–10 días hábiles en aparecer en tu tarjeta.`,
    cancelledNoRefund: 'No se emitió reembolso. Contacta a tu entrenador si tienes preguntas.',
    paidOn: 'el',
    attempts: 'intentos',
    installment: 'Pago',
    paymentMethod: 'Método de pago',
    cardOnFile: 'Hay una tarjeta registrada para cobros automáticos.',
    cardMissing: 'No hay tarjeta registrada. Por favor contacta a tu entrenador.',
    paidInFull: 'Pagado en su totalidad \u2014 ¡gracias!',
    updateCard: 'Actualizar método de pago',
    updateCardOpening: 'Abriendo portal seguro\u2026',
    updateCardError: 'No se pudo abrir el portal de pagos. Intenta de nuevo o contacta a tu entrenador.',
    pastDueHeading: 'Hay un pago atrasado.',
    pastDueBody: 'Intentamos cobrar tu tarjeta pero no se procesó. Volveremos a intentar automáticamente, pero puedes actualizar tu tarjeta.',
    lastFailure: 'Último error',
    contactCoach: '¿Dudas? Contacta a tu entrenador.',
    poweredBy: 'Scrimly',
    // Edit flow
    editHeading: 'Actualizar tu información',
    editIntro: '¿Necesitas cambiar un número, contacto de emergencia o notas médicas? Te enviaremos un enlace seguro al correo registrado.',
    editSendButton: 'Enviarme un enlace para editar',
    editSending: 'Enviando\u2026',
    editSentTitle: 'Revisa tu correo',
    editSentBody: (emailMasked) => `Enviamos un enlace seguro a ${emailMasked}. Es válido por 15 minutos y solo puede usarse una vez.`,
    editSendFailed: 'No se pudo enviar el correo. Intenta de nuevo o contacta a tu entrenador.',
    editModeHeading: 'Editar tu información',
    editModeIntro: 'Actualiza los campos abajo. Los cambios se guardan al presionar "Guardar".',
    editParentName: 'Tu nombre',
    editParentPhone: 'Tu teléfono',
    editParentEmailReadOnly: 'Tu correo (contacta a tu entrenador para cambiarlo)',
    editEmergencyHeading: 'Contacto de emergencia',
    editEmergencyName: 'Nombre',
    editEmergencyPhone: 'Teléfono',
    editEmergencyRelationship: 'Parentesco',
    editMedicalHeading: 'Notas médicas',
    editMedicalHint: 'Alergias, condiciones o información que el entrenador deba saber.',
    editSave: 'Guardar cambios',
    editSaving: 'Guardando\u2026',
    editCancel: 'Cancelar',
    editSaveFailed: 'Error al guardar. El enlace puede haber expirado — solicita uno nuevo.',
    editSaved: 'Tus cambios fueron guardados.',
    editTokenInvalid: 'Este enlace no es válido o expiró. Solicita uno nuevo desde tu página de registro.',
    calHeading: 'Suscríbete al calendario del equipo',
    calIntro: 'Agrega cada práctica y partido a tu calendario. Se actualiza automáticamente cuando tu entrenador cambia el horario.',
    calSubscribe: 'Suscribirse',
    calAddToApple: 'Agregar a Calendario de Apple',
    calAddToGoogle: 'Agregar a Google Calendar',
    calCopyLink: 'Copiar enlace',
    calCopied: '¡Copiado!',
    // SMS preferences card (ES)
    smsHeading: 'Mensajes de texto',
    smsOnLead: (phone) => `Estás suscrito al ${phone}.`,
    smsOnBody: 'Recibirás recordatorios de partidos, confirmaciones, cancelaciones y alertas del clima.',
    smsOffLead: 'Los mensajes de texto están desactivados.',
    smsOffBody: (phone) => `Actívalos para recibir recordatorios, confirmaciones y alertas del clima al ${phone}.`,
    smsNoPhoneBody: 'Primero agrega un número de teléfono a tu información de contacto y vuelve aquí para activar los mensajes de texto.',
    smsTurnOn: 'Activar mensajes de texto',
    smsTurnOff: 'Desactivar mensajes de texto',
    smsSaving: 'Guardando\u2026',
    smsConfirmTitle: '¿Activar mensajes de texto?',
    smsConfirmDisclosure: 'Al activar esta opción, aceptas recibir mensajes de texto automáticos de Scrimly sobre esta inscripción, incluyendo cancelaciones por clima, cambios de horario, confirmaciones y recordatorios de pago, al número registrado. Pueden aplicarse tarifas de mensajes y datos. Responde STOP para cancelar, HELP para ayuda. El consentimiento no es requisito para la inscripción.',
    smsConfirmAgree: 'Acepto \u2014 activar',
    smsConfirmCancel: 'Cancelar',
    smsError: 'No se pudieron actualizar tus preferencias de mensajes. Inténtalo de nuevo.',
  },
};

function fmtMoney(cents) {
  if (cents == null) return '—';
  return '$' + (Number(cents) / 100).toFixed(2).replace(/\.00$/, '');
}

export default function ParentPortal() {
  const { registrationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editToken = searchParams.get('edit');
  const { i18n } = useTranslation();
  const lang = String(i18n.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const tr = T[lang];
  const dateLocale = lang === 'es' ? esLocale : undefined;

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [portalOpening, setPortalOpening] = useState(false);
  const [portalError,   setPortalError]   = useState(null);

  // Magic-link edit flow state. `requestState` is the "send me a link" button
  // lifecycle; `editMode` is true once the parent has opened the link.
  const [requestState, setRequestState] = useState({ status: 'idle', emailMasked: null, error: null });
  const [editForm, setEditForm] = useState(null); // populated when editMode is active
  const [editSaving, setEditSaving] = useState(false);
  const [editSaveError, setEditSaveError] = useState(null);
  const [editSaved, setEditSaved] = useState(false);
  const [editTokenInvalid, setEditTokenInvalid] = useState(false);
  const editMode = !!editToken;
  const [calCopied, setCalCopied] = useState(false);

  // SMS preferences — lifecycle: idle → confirming (disclosure modal, opt-in
  // only) → saving → idle. Errors surface below the button. We keep the
  // toggle state optimistically local so the UI feels instant; on failure we
  // revert + show the error.
  const [smsSaving,    setSmsSaving]    = useState(false);
  const [smsConfirm,   setSmsConfirm]   = useState(false); // shows disclosure modal
  const [smsError,     setSmsError]     = useState(null);
  const [smsOptInLocal,setSmsOptInLocal]= useState(null); // null = defer to data

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const call = httpsCallable(getFunctions(), 'getParentPortalData');
        const res  = await call({ registrationId });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [registrationId]);

  // When the URL carries an `?edit=<token>`, fetch the current editable
  // values so we can pre-populate the edit form. Any token error (expired,
  // already used, bad signature) flips editTokenInvalid so the UI can show
  // a recovery prompt instead of a broken form.
  useEffect(() => {
    if (!editMode || !registrationId) return;
    let cancelled = false;
    (async () => {
      try {
        const call = httpsCallable(getFunctions(), 'getParentEditableData');
        const res  = await call({ registrationId, token: editToken });
        if (!cancelled) setEditForm(res.data);
      } catch (err) {
        if (!cancelled) setEditTokenInvalid(true);
      }
    })();
    return () => { cancelled = true; };
  }, [editMode, registrationId, editToken]);

  // "Email me an edit link" — Step 1 of the passwordless flow.
  const requestEditLink = async () => {
    if (requestState.status === 'loading') return;
    setRequestState({ status: 'loading', emailMasked: null, error: null });
    try {
      const call = httpsCallable(getFunctions(), 'requestParentEditLink');
      const res  = await call({ registrationId });
      setRequestState({ status: 'sent', emailMasked: res?.data?.email || null, error: null });
    } catch (err) {
      setRequestState({ status: 'error', emailMasked: null, error: err?.message || tr.editSendFailed });
    }
  };

  // Save the edit form — Step 2 of the passwordless flow. On success we drop
  // the edit token from the URL and reload the portal data to reflect any
  // changes (e.g. updated emergency contact) if we ever surface them.
  const saveEdits = async () => {
    if (editSaving || !editForm) return;
    setEditSaving(true);
    setEditSaveError(null);
    try {
      const call = httpsCallable(getFunctions(), 'submitParentEdits');
      await call({
        registrationId,
        token: editToken,
        updates: {
          parentName:   editForm.parentName,
          parentPhone:  editForm.parentPhone,
          medicalNotes: editForm.medicalNotes || null,
          emergencyContact: {
            name:         editForm.emergencyContact?.name || '',
            phone:        editForm.emergencyContact?.phone || '',
            relationship: editForm.emergencyContact?.relationship || '',
          },
        },
      });
      setEditSaved(true);
      // Strip the token from the URL so a page refresh doesn't re-attempt to
      // use it (it's single-use on the server anyway).
      setSearchParams({}, { replace: true });
    } catch (err) {
      setEditSaveError(err?.message || tr.editSaveFailed);
    } finally {
      setEditSaving(false);
    }
  };

  const updateEditField = (path, value) => {
    setEditForm(prev => {
      if (!prev) return prev;
      if (path.includes('.')) {
        const [root, key] = path.split('.');
        return { ...prev, [root]: { ...(prev[root] || {}), [key]: value } };
      }
      return { ...prev, [path]: value };
    });
  };

  // Stripe Customer Portal — only surfaced for installment plan parents, since
  // those are the only ones with a saved stripeCustomerId. Pay-in-full parents
  // have nothing to manage, so we keep the UI clean and hide the button.
  const openBillingPortal = async () => {
    if (portalOpening) return;
    setPortalOpening(true);
    setPortalError(null);
    try {
      const call = httpsCallable(getFunctions(), 'createParentBillingPortalSession');
      const res  = await call({
        registrationId,
        returnUrl: window.location.href,
      });
      const url = res?.data?.url;
      if (!url) throw new Error('no url');
      // Full-page redirect — Stripe portal must be top-level, not an iframe.
      window.location.href = url;
    } catch (err) {
      setPortalError(tr.updateCardError);
      setPortalOpening(false);
    }
  };

  // Flip SMS opt-in. For opting IN we require the parent to confirm the TCPA
  // disclosure first (modal → handleSmsOptIn(true)). For opting OUT we call
  // immediately — opt-outs are always honored without ceremony, consistent
  // with TCPA guidance. Server stamps the audit fields (consent text, IP,
  // UA, timestamp) so we never have to trust a client-submitted record.
  const submitSmsOptIn = async (optIn) => {
    if (smsSaving) return;
    setSmsSaving(true);
    setSmsError(null);
    try {
      const call = httpsCallable(getFunctions(), 'updateSmsOptIn');
      const res  = await call({ registrationId, optIn, lang });
      setSmsOptInLocal(!!res?.data?.smsOptIn);
      setSmsConfirm(false);
    } catch (err) {
      setSmsError(err?.message || tr.smsError);
    } finally {
      setSmsSaving(false);
    }
  };

  const fmtDate = (ms) => {
    if (!ms) return null;
    return format(new Date(ms), 'MMM d, yyyy', dateLocale ? { locale: dateLocale } : undefined);
  };

  // Derived totals for the header cards.
  const totals = useMemo(() => {
    if (!data?.schedule) return { paid: 0, remaining: 0 };
    const paid = data.schedule.filter(s => s.status === 'paid').reduce((a, s) => a + (s.amount || 0), 0);
    const remaining = data.schedule
      .filter(s => s.status !== 'paid' && s.status !== 'cancelled')
      .reduce((a, s) => a + (s.amount || 0), 0);
    return { paid, remaining };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] text-zinc-500 text-sm">
        {tr.loading}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-zinc-300" />
          <h1
            className="mt-4 text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {tr.notFound}
          </h1>
          <p className="mt-2 text-sm text-zinc-600">{tr.notFoundHint}</p>
        </div>
      </div>
    );
  }

  const hasPastDue = data.status === 'past_due' || data.schedule?.some(s => s.status === 'failed');
  const isCancelled = data.status === 'cancelled';

  // ── Edit mode — magic-link flow ────────────────────────────────────────────
  // Takes over the whole page when ?edit=<token> is present. Renders a
  // lightweight form scoped to the EDITABLE_FIELDS whitelist enforced server-
  // side. The parent can cancel back to the normal portal view at any time.
  if (editMode && !editSaved) {
    // Shared input class so each form field uses the same lime focus
    // ring + border. Cleaner than repeating the long string per field
    // and easier to audit if we ever need to change it.
    const editInputCls =
      'w-full rounded-xl border border-zinc-300 px-3 py-2 text-sm text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 transition';
    if (editTokenInvalid) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] px-6">
          <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-8 text-center">
            <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-zinc-300" />
            <p className="mt-4 text-sm text-zinc-700">{tr.editTokenInvalid}</p>
            <button
              type="button"
              onClick={() => navigate(`/r/${registrationId}`, { replace: true })}
              className="mt-4 text-sm font-bold uppercase text-[#5a8a1f] hover:text-[#3d6010]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              ← {tr.titlePrefix} {data.playerName}
            </button>
          </div>
        </div>
      );
    }
    if (!editForm) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#f7faf5] text-zinc-500 text-sm">
          {tr.loading}
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#f7faf5] py-8 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Parent portal
            </p>
            <h1
              className="mt-1 text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.editModeHeading}
            </h1>
            <p className="mt-1 text-sm text-zinc-600">{tr.editModeIntro}</p>
          </div>

          <div className="space-y-5">
            {/* Parent contact */}
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-[#0d1f14] mb-1">{tr.editParentName}</label>
                <input type="text" value={editForm.parentName}
                  onChange={(e) => updateEditField('parentName', e.target.value)}
                  className={editInputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0d1f14] mb-1">{tr.editParentPhone}</label>
                <input type="tel" value={editForm.parentPhone}
                  onChange={(e) => updateEditField('parentPhone', e.target.value)}
                  className={editInputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-zinc-500 mb-1">{tr.editParentEmailReadOnly}</label>
                <input type="email" value={editForm.parentEmail} readOnly
                  className="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-500" />
              </div>
            </div>

            {/* Emergency contact */}
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-3">
              <h2
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.editEmergencyHeading}
              </h2>
              <div>
                <label className="block text-xs font-semibold text-[#0d1f14] mb-1">{tr.editEmergencyName}</label>
                <input type="text" value={editForm.emergencyContact?.name || ''}
                  onChange={(e) => updateEditField('emergencyContact.name', e.target.value)}
                  className={editInputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0d1f14] mb-1">{tr.editEmergencyPhone}</label>
                <input type="tel" value={editForm.emergencyContact?.phone || ''}
                  onChange={(e) => updateEditField('emergencyContact.phone', e.target.value)}
                  className={editInputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#0d1f14] mb-1">{tr.editEmergencyRelationship}</label>
                <input type="text" value={editForm.emergencyContact?.relationship || ''}
                  onChange={(e) => updateEditField('emergencyContact.relationship', e.target.value)}
                  className={editInputCls} />
              </div>
            </div>

            {/* Medical notes */}
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-2">
              <h2
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.editMedicalHeading}
              </h2>
              <p className="text-xs text-zinc-500">{tr.editMedicalHint}</p>
              <textarea rows={4} value={editForm.medicalNotes || ''}
                onChange={(e) => updateEditField('medicalNotes', e.target.value)}
                className={editInputCls} />
            </div>

            {editSaveError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {editSaveError}
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate(`/r/${registrationId}`, { replace: true })}
                className="text-sm font-bold uppercase text-zinc-500 hover:text-[#0d1f14] transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {tr.editCancel}
              </button>
              <button
                type="button"
                onClick={saveEdits}
                disabled={editSaving}
                className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-wait"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {editSaving ? tr.editSaving : tr.editSave}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7faf5] py-8 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">

        {editSaved && (
          <div className="mb-5 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-4 py-3 text-sm text-[#3d6010] flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f]" />
            {tr.editSaved}
          </div>
        )}

        {/* Pitch-dark hero — mirrors RegisterPage so the parent's first-
            touch experience extends seamlessly into ongoing engagement.
            The team identity carries through with a lime-ringed avatar
            medallion (initial letter or, in a future pass, team logo). */}
        <div
          className="mb-6 relative overflow-hidden rounded-2xl bg-[#0d1f14] px-5 py-6 shadow-[0_2px_6px_0_rgba(13,31,20,0.18)]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        >
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#b5f03d] ring-2 ring-[#b5f03d]/30 flex items-center justify-center flex-shrink-0">
              <UserCircleIcon className="h-6 w-6 text-[#0d1f14]" />
            </div>
            <div className="min-w-0">
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {data.teamName || 'Parent portal'}{data.contextName ? ` · ${data.contextName}` : ''}
              </p>
              <h1
                className="mt-1 text-xl sm:text-2xl font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.titlePrefix} {data.playerName}
              </h1>
            </div>
          </div>
        </div>

        {/* Cancelled banner — supersedes the past-due banner */}
        {isCancelled && (
          <div className="mb-5 rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-zinc-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-[#0d1f14]">{tr.cancelledHeading}</p>
                <p className="mt-1 text-xs text-zinc-600">
                  {data.cancelRefundTotal > 0
                    ? tr.cancelledRefunded(fmtMoney(data.cancelRefundTotal))
                    : tr.cancelledNoRefund}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Past-due banner — only shown if not cancelled */}
        {!isCancelled && hasPastDue && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex items-start gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800">{tr.pastDueHeading}</p>
                <p className="mt-1 text-xs text-red-700">{tr.pastDueBody}</p>
                {data.lastFailureReason && (
                  <p className="mt-2 text-xs text-red-600">
                    <span className="font-medium">{tr.lastFailure}:</span> {data.lastFailureReason}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Summary cards — left card uses lime accents (paid feels positive),
            right card stays neutral. The big number uses HEAD_FONT to read
            as a hero metric. */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4">
            <p
              className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.totalPaid}
            </p>
            <p
              className="mt-1 text-2xl font-bold text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {fmtMoney(totals.paid)}
            </p>
            <p className="mt-0.5 text-xs text-zinc-500">
              {data.installmentsPaid}/{data.installmentsTotal} {tr.installment.toLowerCase()}
            </p>
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4">
            <p
              className="text-[10px] font-bold uppercase tracking-[1.2px] text-zinc-500"
              style={{ fontFamily: HEAD_FONT }}
            >
              {tr.remaining}
            </p>
            <p
              className="mt-1 text-2xl font-bold text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {fmtMoney(totals.remaining)}
            </p>
            {data.nextChargeDueDate && (
              <p className="mt-0.5 text-xs text-zinc-500">
                {tr.nextCharge}: {fmtDate(data.nextChargeDueDate)}
              </p>
            )}
          </div>
        </div>

        {/* Schedule — installment-by-installment timeline. Status badges
            keep their semantic colors (emerald = paid, red = failed,
            amber = partial-refund) since those signals need to read fast
            regardless of brand. */}
        {Array.isArray(data.schedule) && data.schedule.length > 0 && (
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-zinc-100">
              <h2
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.scheduleHeading}
              </h2>
            </div>
            <ul className="divide-y divide-zinc-100">
              {data.schedule.map((slot) => {
                const n = slot.index + 1;
                const status = slot.status || 'pending';
                return (
                  <li key={slot.index} className="flex items-center justify-between px-4 py-3 hover:bg-[#b5f03d]/[0.03] transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0d1f14]">
                        {tr.installment} {n}
                        <span className="ml-2 text-zinc-600 font-normal">{fmtMoney(slot.amount)}</span>
                      </p>
                      <p className="mt-0.5 text-xs text-zinc-500">
                        {status === 'paid' && slot.paidAt
                          ? `${tr.statusPaid} ${tr.paidOn} ${fmtDate(slot.paidAt)}`
                          : slot.dueDate
                            ? fmtDate(slot.dueDate)
                            : ''}
                      </p>
                    </div>

                    {status === 'paid' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-emerald-700"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        {tr.statusPaid}
                      </span>
                    ) : status === 'refunded' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-zinc-500"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        <CheckCircleIcon className="h-4 w-4 text-zinc-300" />
                        {tr.statusRefunded}
                      </span>
                    ) : status === 'partially_refunded' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase text-amber-700"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        title={slot.refundedAmount ? `−${fmtMoney(slot.refundedAmount)}` : ''}
                      >
                        {tr.statusPartiallyRefunded}
                        {slot.refundedAmount && (
                          <span className="text-zinc-400 font-normal ml-1 normal-case">
                            (−{fmtMoney(slot.refundedAmount)})
                          </span>
                        )}
                      </span>
                    ) : status === 'failed' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase text-red-700"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        title={slot.failureReason || ''}
                      >
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        {tr.statusFailed}
                        {slot.attemptCount > 0 && (
                          <span className="text-zinc-400 font-normal ml-1 normal-case">
                            ({slot.attemptCount} {tr.attempts})
                          </span>
                        )}
                      </span>
                    ) : status === 'cancelled' ? (
                      <span
                        className="text-xs font-bold uppercase text-zinc-400"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {tr.statusCancelled}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase text-zinc-600"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        <ClockIcon className="h-4 w-4" />
                        {tr.statusPending}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Payment method status — three-state:
              1. Pay-in-full + paid → "Paid in full" (no card section needed,
                 one-time payers have nothing to manage).
              2. Installment plan + card on file → "Card on file" + billing
                 portal button.
              3. Installment plan + NO card → "No card is on file" warning
                 (this is the genuine error state coaches need to see). */}
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-4 mb-8">
          <div className="flex items-start gap-3">
            {data.isPayInFull && data.isPaidInFull
              ? <div className="h-8 w-8 rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center shrink-0">
                  <CheckCircleIcon className="h-4 w-4 text-[#5a8a1f]" />
                </div>
              : <div className="h-8 w-8 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                  <CreditCardIcon className="h-4 w-4 text-zinc-500" />
                </div>}
            <div className="flex-1">
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {tr.paymentMethod}
              </p>
              <p className={`mt-0.5 text-sm ${
                data.isPayInFull && data.isPaidInFull ? 'text-[#5a8a1f] font-semibold' : 'text-[#0d1f14]'
              }`}>
                {data.isPayInFull && data.isPaidInFull
                  ? tr.paidInFull
                  : data.hasPaymentMethod
                    ? tr.cardOnFile
                    : tr.cardMissing}
              </p>

              {/* Card-on-file → Stripe Customer Portal. Only shown when a saved
                  card exists; pay-in-full parents have nothing to manage. Also
                  hidden on cancelled registrations. */}
              {data.hasPaymentMethod && !isCancelled && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={openBillingPortal}
                    disabled={portalOpening}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3.5 py-2 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-wait"
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {portalOpening ? tr.updateCardOpening : `${tr.updateCard} →`}
                  </button>
                  {portalError && (
                    <p className="mt-2 text-xs text-red-600">{portalError}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SMS preferences — TCPA-compliant opt-in/out. Turning ON requires
            an explicit click-through on the disclosure modal. Turning OFF is
            immediate (opt-outs always honored without friction). Hidden on
            cancelled registrations — nothing useful to toggle. */}
        {!isCancelled && (() => {
          // Prefer optimistic local state if we've toggled this session;
          // otherwise fall through to the server value from getParentPortalData.
          const smsOn       = smsOptInLocal != null ? smsOptInLocal : !!data.smsOptIn;
          const phoneMasked = data.parentPhoneMasked || null;
          const hasPhone    = !!data.hasParentPhone;
          return (
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-zinc-500" />
                </div>
                <div className="flex-1">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {tr.smsHeading}
                  </p>

                  {!hasPhone ? (
                    <p className="mt-0.5 text-sm text-zinc-600">{tr.smsNoPhoneBody}</p>
                  ) : smsOn ? (
                    <>
                      <p className="mt-0.5 text-sm text-[#5a8a1f] font-semibold">
                        {tr.smsOnLead(phoneMasked || '')}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">{tr.smsOnBody}</p>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => submitSmsOptIn(false)}
                          disabled={smsSaving}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3.5 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors disabled:opacity-60 disabled:cursor-wait"
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        >
                          {smsSaving ? tr.smsSaving : tr.smsTurnOff}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mt-0.5 text-sm text-[#0d1f14] font-semibold">{tr.smsOffLead}</p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {tr.smsOffBody(phoneMasked || '')}
                      </p>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => { setSmsError(null); setSmsConfirm(true); }}
                          disabled={smsSaving}
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-3.5 py-2 text-xs font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-wait"
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                        >
                          {tr.smsTurnOn} →
                        </button>
                      </div>
                    </>
                  )}

                  {smsError && (
                    <p className="mt-2 text-xs text-red-600">{smsError}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* TCPA opt-in confirmation modal — shown when a parent clicks
            "Turn on text updates". We require the explicit click-through so
            the server-side audit trail can truthfully record "parent saw THIS
            exact disclosure text and clicked I agree". Matches the consent
            shape at registration (RegisterPage.jsx). */}
        {smsConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm px-4 py-6"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) { setSmsConfirm(false); setSmsError(null); } }}
          >
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-100">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  TCPA consent
                </p>
                <h2
                  className="mt-0.5 text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {tr.smsConfirmTitle}
                </h2>
              </div>
              <div className="px-5 py-4">
                <p className="text-xs leading-5 text-zinc-600">{tr.smsConfirmDisclosure}</p>
                {smsError && (
                  <p className="mt-3 text-xs text-red-600">{smsError}</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-3 bg-zinc-50 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => { setSmsConfirm(false); setSmsError(null); }}
                  disabled={smsSaving}
                  className="text-sm font-bold uppercase text-zinc-600 hover:text-[#0d1f14] px-3 py-1.5 disabled:opacity-60 transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {tr.smsConfirmCancel}
                </button>
                <button
                  type="button"
                  onClick={() => submitSmsOptIn(true)}
                  disabled={smsSaving}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.97] transition-all disabled:opacity-60 disabled:cursor-wait"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                >
                  {smsSaving ? tr.smsSaving : tr.smsConfirmAgree}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar subscription — pretty URL via hosting rewrite to the
            parentCalendarFeed function. `webcal://` triggers one-click
            subscribe in Apple Calendar; Google Calendar expects https. We
            offer both plus a manual copy button for Outlook / other clients. */}
        {!isCancelled && (() => {
          const base = (typeof window !== 'undefined' ? window.location.origin : 'https://app.getscrimly.com');
          const httpsUrl  = `${base}/calendar/${registrationId}.ics`;
          const webcalUrl = httpsUrl.replace(/^https?:\/\//, 'webcal://');
          const gcalUrl   = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(httpsUrl)}`;
          return (
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                  <CalendarDaysIcon className="h-4 w-4 text-zinc-500" />
                </div>
                <div className="flex-1">
                  <p
                    className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                    style={{ fontFamily: HEAD_FONT }}
                  >
                    {tr.calHeading}
                  </p>
                  <p className="mt-0.5 text-sm text-zinc-700">{tr.calIntro}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a
                      href={webcalUrl}
                      className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {tr.calAddToApple}
                    </a>
                    <a
                      href={gcalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {tr.calAddToGoogle}
                    </a>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(httpsUrl);
                          setCalCopied(true);
                          setTimeout(() => setCalCopied(false), 2000);
                        } catch { /* no-op */ }
                      }}
                      className="inline-flex items-center gap-1 rounded-xl border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {calCopied ? tr.calCopied : tr.calCopyLink}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Update your info — passwordless magic-link edit flow. Hidden on
            cancelled registrations (nothing useful to edit) and for pay-in-
            full reg without a parentEmail. */}
        {!isCancelled && (
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-4 py-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
                <PencilSquareIcon className="h-4 w-4 text-zinc-500" />
              </div>
              <div className="flex-1">
                <p
                  className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {tr.editHeading}
                </p>
                <p className="mt-0.5 text-sm text-zinc-700">{tr.editIntro}</p>

                {requestState.status === 'sent' ? (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 px-3 py-2">
                    <EnvelopeIcon className="h-4 w-4 text-[#5a8a1f] shrink-0 mt-0.5" />
                    <div>
                      <p
                        className="text-xs font-bold uppercase text-[#3d6010]"
                        style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                      >
                        {tr.editSentTitle}
                      </p>
                      <p className="mt-0.5 text-xs text-[#3d6010]">
                        {tr.editSentBody(requestState.emailMasked || '')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={requestEditLink}
                      disabled={requestState.status === 'loading'}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3.5 py-2 text-xs font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors disabled:opacity-60 disabled:cursor-wait"
                      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                    >
                      {requestState.status === 'loading' ? tr.editSending : `${tr.editSendButton} →`}
                    </button>
                    {requestState.status === 'error' && (
                      <p className="mt-2 text-xs text-red-600">{requestState.error || tr.editSendFailed}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-zinc-500">
          {tr.contactCoach} · <span className="font-bold uppercase text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>{tr.poweredBy}</span>
        </p>
      </div>
    </div>
  );
}
